"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { auth, db } from "../firebase"
import { ref, onValue, update, get } from "../lib/firebase-database"
import { Search, X, Calendar, AlertTriangle } from 'lucide-react'
import { Input } from "./ui/input"
import { Button } from "./ui/button"
import { useAuth } from "../hooks/useAuth"
import { OwnerLayout } from "./OwnerLayout"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog"
import { InvoiceGenerator } from "./InvoiceGenerator"

export function OwnerBookingPage() {
  const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false)
  const [bookings, setBookings] = useState([])
  const [filteredBookings, setFilteredBookings] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState("")
  const [activeCategory, setActiveCategory] = useState("all")
  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const [bookingToCancel, setBookingToCancel] = useState(null)
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false)
  const [selectedInvoiceData, setSelectedInvoiceData] = useState(null)
  const [selectedVillaId, setSelectedVillaId] = useState(null)
  const [ownedVillas, setOwnedVillas] = useState([])
  const [hasNewRequests, setHasNewRequests] = useState(false);
  const [viewedRequests, setViewedRequests] = useState(new Set());
  const [requests, setRequests] = useState([]);
  const user = auth.currentUser
  const navigate = useNavigate()
  const { isOwner } = useAuth()

  // Fetch owner's bookings with villa data
  useEffect(() => {
    if (!user) {
      navigate("/login")
      return
    }

    if (!isOwner) {
      navigate("/")
      return
    }

    fetchOwnerBookings()
  }, [user, navigate, isOwner, selectedVillaId])

  useEffect(() => {
    if (!user) return

    const villasRef = ref(db, "villas")
    const unsubscribe = onValue(villasRef, (snapshot) => {
      const villasData = snapshot.val()
      if (villasData) {
        const userVillas = Object.entries(villasData)
          .filter(([_, villa]) => villa.ownerEmail === user.email)
          .map(([id, data]) => ({ id, ...data }))
        setOwnedVillas(userVillas)

        if (!selectedVillaId && userVillas.length > 0) {
          setSelectedVillaId(userVillas[0].id)
        }
      }
    })

    return () => unsubscribe()
  }, [user])

  useEffect(() => {
    if (!user) return;

    const requestsRef = ref(db, "cancellationRequests");
    const unsubscribe = onValue(requestsRef, (snapshot) => {
      const data = snapshot.val();
      if (!data) return;

      const requests = Object.entries(data).map(([id, request]) => ({
        id,
        ...request
      }));

      // Check for new unviewed requests
      const hasNew = requests.some(request => 
        request.status === "pending" && 
        !viewedRequests.has(request.id)
      );

      setHasNewRequests(hasNew);
    });

    return () => unsubscribe();
  }, [user, viewedRequests]);

  const fetchOwnerBookings = async () => {
    try {
      if (!user || !selectedVillaId) return;

      const ownerBookingsRef = ref(db, `bookings/${user.uid}`);
      const unsubscribe = onValue(ownerBookingsRef, async (snapshot) => {
        const data = snapshot.val();
        if (!data) {
          setBookings([]);
          setFilteredBookings([]);
          setLoading(false);
          return;
        }

        const bookingsArray = await Promise.all(
          Object.entries(data)
            .filter(([_, booking]) => booking.villaId === selectedVillaId)
            .map(async ([id, booking]) => {
              const villaRef = ref(db, `villas/${booking.villaId}`);
              const villaSnapshot = await get(villaRef);
              const villaData = villaSnapshot.val();

              return {
                id,
                ...booking,
                villaData: {
                  name: villaData?.name || "Villa Name Not Available",
                  thumbnail: villaData?.thumbnail || "/placeholder.svg",
                  address: villaData?.address || "Address not available",
                }
              };
            })
        );

        setBookings(bookingsArray);
        setFilteredBookings(bookingsArray);
        setLoading(false);
      });

      return () => unsubscribe();
    } catch (error) {
      console.error("Error fetching owner bookings:", error);
      setLoading(false);
    }
  }

  const formatDate = (timestamp, timing) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp);
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    const formattedDate = `${days[date.getDay()]}, ${months[date.getMonth()]} ${date.getDate()}, ${String(date.getFullYear()).slice(-2)}`;
    return timing ? `${formattedDate} (${timing})` : formattedDate;
  };

  const handleCancelBooking = (booking) => {
    setBookingToCancel(booking)
    setShowCancelDialog(true)
  }

  const shareBooking = (booking) => {
    const shareText = `Booking at ${booking.villaData?.name}\nCheck-in: ${formatDate(
      booking.checkIn,
      booking.checkInTiming,
    )}\nCheck-out: ${formatDate(booking.checkOut, booking.checkOutTiming)}\nPhone: ${booking.phoneNumber || "N/A"}`
    if (navigator.share) {
      navigator.share({
        title: "Booking Details",
        text: shareText,
      })
    }
  }

  const confirmCancelBooking = async () => {
    if (!bookingToCancel) return

    try {
      await update(ref(db, `bookings/${user.uid}/${bookingToCancel.id}`), {
        status: "cancelled",
        cancelledAt: Date.now(),
        cancelledBy: user.uid,
      })

      if (bookingToCancel.userId) {
        await update(ref(db, `bookings/${bookingToCancel.userId}/${bookingToCancel.id}`), {
          status: "cancelled",
          cancelledAt: Date.now(),
          cancelledBy: user.uid,
        })
      }

      await update(ref(db, `allBookings/${bookingToCancel.id}`), {
        status: "cancelled",
        cancelledAt: Date.now(),
        cancelledBy: user.uid,
      })

      setBookings((prevBookings) =>
        prevBookings.map((booking) =>
          booking.id === bookingToCancel.id
            ? { ...booking, status: "cancelled", cancelledAt: Date.now(), cancelledBy: user.uid }
            : booking,
        ),
      )

      setShowCancelDialog(false)
      setBookingToCancel(null)
    } catch (error) {
      console.error("Error cancelling booking:", error)
    }
  }

  // Generate invoice with complete booking data
  const handleGenerateInvoice = (booking) => {
    const invoiceData = {
      villaName: booking.villaData?.name,
      villaAddress: booking.villaData?.address,
      invoiceNo: booking.bookingId || `INV-${Date.now().toString().slice(-6)}`,
      dateIssued: formatDate(booking.createdAt || Date.now()),
      bookings: [
        {
          bookingId: booking.bookingId,
          guestName: booking.userName || "Guest",
          checkIn: booking.checkIn,
          checkInTiming: booking.checkInTiming,
          checkOut: booking.checkOut,
          checkOutTiming: booking.checkOutTiming,
          guestCount: booking.guests,
          amount: booking.amount,
          phoneNumber: booking.phoneNumber || "N/A",
        },
      ],
      website: "www.villakosh.com",
      email: "support@villakosh.com",
      phone: "+91 98765 43210",
    }

    setSelectedInvoiceData(invoiceData)
    setShowInvoiceDialog(true)
  }

  useEffect(() => {
    if (!bookings) return;

    let filtered = [...bookings].filter(booking => booking.villaId === selectedVillaId);

    if (searchQuery.trim()) {
      filtered = filtered.filter((booking) =>
        booking.bookingId?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (activeCategory === "upcoming") {
      filtered = filtered
        .filter((booking) => new Date(booking.checkIn) > tomorrow)
        .sort((a, b) => {
          if (a.status === "cancelled" && b.status !== "cancelled") return 1;
          if (a.status !== "cancelled" && b.status === "cancelled") return -1;
          return new Date(a.checkIn) - new Date(b.checkIn);
        });
    } else if (activeCategory === "today") {
      filtered = filtered
        .filter((booking) => {
          const checkIn = new Date(booking.checkIn);
          return checkIn.toDateString() === today.toDateString();
        })
        .sort((a, b) => {
          if (a.status === "cancelled" && b.status !== "cancelled") return 1;
          if (a.status !== "cancelled" && b.status === "cancelled") return -1;
          return 0;
        });
    } else if (activeCategory === "past") {
      filtered = filtered
        .filter((booking) => new Date(booking.checkOut) < today)
        .sort((a, b) => {
          if (a.status === "cancelled" && b.status !== "cancelled") return 1;
          if (a.status !== "cancelled" && b.status === "cancelled") return -1;
          return new Date(b.checkOut) - new Date(a.checkOut);
        });
    } else if (activeCategory === "cancelled") {
      filtered = filtered.filter((booking) => booking.status === "cancelled");
    }

    setFilteredBookings(filtered);
  }, [searchQuery, bookings, activeCategory, selectedVillaId])

  const getCategoryCount = (category) => {
    if (!bookings) return 0

    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const tomorrow = new Date(today)
    tomorrow.setDate(tomorrow.getDate() + 1)

    switch (category) {
      case "upcoming":
        return bookings.filter((b) => new Date(b.checkIn) > tomorrow).length
      case "today":
        return bookings.filter((b) => {
          const checkIn = new Date(b.checkIn)
          return checkIn.toDateString() === today.toDateString()
        }).length
      case "past":
        return bookings.filter((b) => new Date(b.checkOut) < today).length
      case "cancelled":
        return bookings.filter((b) => b.status === "cancelled").length
      default:
        return bookings.length
    }
  }

  const canCancelBooking = (booking) => {
    const today = new Date()
    const checkIn = new Date(booking.checkIn)
    return (
      checkIn > today &&
      booking.status !== "cancelled"
    )
  }

  const VillaSelector = ({ villas, selectedVillaId, onVillaSelect }) => (
    <div className="flex gap-2 mb-4 md:mb-6 overflow-x-auto pb-2 -mx-1">
      {villas.map((villa) => (
        <Button
          key={villa.id}
          variant={selectedVillaId === villa.id ? "default" : "outline"}
          onClick={() => onVillaSelect(villa.id)}
          className="text-xs md:text-sm whitespace-nowrap px-2 md:px-3 py-1 md:py-2"
        >
          {villa.name}
        </Button>
      ))}
    </div>
  )

  return (
    <OwnerLayout user={user} onLogout={() => setShowLogoutConfirmation(true)}>
      <div className="min-h-screen bg-gray-50 md:bg-gray-100">
        <main className="max-w-7xl mx-auto py-4 md:py-6 px-3 md:px-6">
          <div className="mb-4 md:mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Your Bookings</h1>
          </div>

          {isOwner && (
            <div className="mb-4 md:mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-gray-400" />
                </div>
                <Input
                  type="text"
                  placeholder="Search by booking ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 pr-9 py-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 block w-full"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <X className="h-4 w-4 text-gray-400 hover:text-gray-500" />
                  </button>
                )}
              </div>
              <Button
                onClick={() => {
                  navigate("/cancellation-requests");
                  // Mark all current requests as viewed
                  setViewedRequests(new Set([...requests.map(r => r.id)]));
                  setHasNewRequests(false);
                }}
                className={`w-full sm:w-auto bg-blue-500 hover:bg-blue-600 text-white flex items-center gap-2 text-sm py-2 relative
                  ${hasNewRequests ? 'animate-pulse' : ''}`}
              >
                <AlertTriangle className="h-4 w-4" />
                Cancellation Requests
                {hasNewRequests && (
                  <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-ping" />
                )}
              </Button>
            </div>
          )}

          <VillaSelector 
            villas={ownedVillas}
            selectedVillaId={selectedVillaId}
            onVillaSelect={setSelectedVillaId}
          />

          <div className="mb-4 md:mb-6 overflow-x-auto -mx-1">
            <div className="flex min-w-full pb-1">
              <button
                onClick={() => setActiveCategory("today")}
                className={`flex-1 whitespace-nowrap py-2 px-2 text-center font-medium text-xs md:text-sm rounded-md mx-1 ${activeCategory === "today" ? "bg-blue-500 text-white" : "bg-white text-gray-700 hover:bg-gray-50"}`}
              >
                Today ({getCategoryCount("today")})
              </button>
              <button
                onClick={() => setActiveCategory("upcoming")}
                className={`flex-1 whitespace-nowrap py-2 px-2 text-center font-medium text-xs md:text-sm rounded-md mx-1 ${activeCategory === "upcoming" ? "bg-blue-500 text-white" : "bg-white text-gray-700 hover:bg-gray-50"}`}
              >
                Upcoming ({getCategoryCount("upcoming")})
              </button>
              <button
                onClick={() => setActiveCategory("past")}
                className={`flex-1 whitespace-nowrap py-2 px-2 text-center font-medium text-xs md:text-sm rounded-md mx-1 ${activeCategory === "past" ? "bg-blue-500 text-white" : "bg-white text-gray-700 hover:bg-gray-50"}`}
              >
                Past ({getCategoryCount("past")})
              </button>
              <button
                onClick={() => setActiveCategory("cancelled")}
                className={`flex-1 whitespace-nowrap py-2 px-2 text-center font-medium text-xs md:text-sm rounded-md mx-1 ${activeCategory === "cancelled" ? "bg-blue-500 text-white" : "bg-white text-gray-700 hover:bg-gray-50"}`}
              >
                Cancelled ({getCategoryCount("cancelled")})
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          ) : filteredBookings.length > 0 ? (
            <div className="grid gap-4 md:gap-6 md:grid-cols-2 lg:grid-cols-3">
              {filteredBookings.map((booking) => (
                <div
                  key={`${booking.userId || "owner"}-${booking.id}`}
                  className={`bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-all ${booking.status === "cancelled" ? "opacity-70" : ""}`}
                >
                  {booking.status === "cancelled" && (
                    <div className="bg-red-500 text-white text-center py-1 font-medium text-xs">CANCELLED</div>
                  )}
                  <div className="relative h-36 md:h-48">
                    <img
                      src={booking.villaData?.thumbnail || "/placeholder.svg"}
                      alt={booking.villaData?.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex flex-col justify-between">
                      <div className="p-3 md:p-4 text-center">
                        <div className="text-white text-xl md:text-2xl font-bold mb-1">VILLAKOSH</div>
                      </div>
                      <div className="p-3 md:p-4 w-full">
                        <h3 className="text-white text-lg md:text-2xl font-bold text-center">{booking.villaData?.name}</h3>
                      </div>
                    </div>
                  </div>

                  <div className="p-3 md:p-4">
                    <div className="flex justify-between mb-3 px-2 md:px-4">
                      <div>
                        <p className="font-semibold text-gray-800 text-base md:text-lg">{booking.userName || "Guest"}</p>
                        <p className="text-xs md:text-sm text-gray-500">Booked on: {formatDate(booking.createdAt)}</p>
                      </div>
                      <div className="flex gap-1 md:gap-2">
                        <button
                          onClick={() => handleGenerateInvoice(booking)}
                          className="flex items-center justify-center px-2 md:px-3 py-1 md:py-2 bg-green-50 text-green-600 rounded-full hover:bg-green-100 transition-colors text-xs"
                          title="Download Invoice"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4 mr-1"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                          <span className="hidden md:inline">Invoice</span>
                        </button>
                        <button
                          onClick={() => shareBooking(booking)}
                          className="flex items-center justify-center px-2 md:px-3 py-1 md:py-2 bg-blue-50 text-blue-600 rounded-full hover:bg-blue-100 transition-colors"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 mb-3 px-2 md:px-4">
                      <div>
                        <p className="text-xs md:text-sm text-gray-500">Check-in</p>
                        <p className="text-xs md:text-base font-medium">{formatDate(booking.checkIn, booking.checkInTiming)}</p>
                      </div>
                      <div>
                        <p className="text-xs md:text-sm text-gray-500">Check-out</p>
                        <p className="text-xs md:text-base font-medium">{formatDate(booking.checkOut, booking.checkOutTiming)}</p>
                      </div>
                      <div>
                        <p className="text-xs md:text-sm text-gray-500">Guests</p>
                        <p className="text-xs md:text-base font-medium">{booking.guests}</p>
                      </div>
                      <div>
                        <p className="text-xs md:text-sm text-gray-500">Amount</p>
                        <p className="text-xs md:text-base font-semibold">₹{booking.amount}</p>
                      </div>
                      <div>
                        <p className="text-xs md:text-sm text-gray-500">Phone</p>
                        <p className="text-xs md:text-base font-medium">{booking.phoneNumber || "N/A"}</p>
                      </div>
                    </div>

                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(booking.villaData?.address)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block mb-3 md:mb-4 px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors mx-2 md:mx-4 rounded text-xs md:text-sm"
                    >
                      <div className="flex items-center gap-2">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 text-blue-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                        <span className="text-blue-600 font-medium truncate">{booking.villaData?.address}</span>
                      </div>
                    </a>

                    <div className="mt-2 pt-3 border-t border-dashed border-gray-300 px-2 md:px-4">
                      <div className="flex flex-col items-end">
                        <div className="w-full flex justify-between items-center">
                          <div>
                            <div className="text-xs text-gray-500">Booking #</div>
                            <div className="text-lg md:text-2xl font-mono font-semibold">
                              #{booking.bookingId?.slice(-6) || "000000"}
                            </div>
                          </div>
                          {canCancelBooking(booking) && (
                            <div className="flex flex-col items-end">
                              <p className="text-xs text-gray-500 mb-1 md:mb-2">Cancel this booking</p>
                              <Button
                                onClick={() => handleCancelBooking(booking)}
                                variant="destructive"
                                className="bg-red-500 hover:bg-red-600 text-white text-xs md:text-sm py-1 md:py-2 px-3 md:px-4"
                              >
                                Cancel
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 md:py-12">
              <Calendar className="h-10 w-10 md:h-12 md:w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 text-base md:text-lg">
                {isOwner && searchQuery
                  ? "No bookings found matching your search."
                  : "No bookings found in this category."}
              </p>
            </div>
          )}
        </main>

        {showCancelDialog && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg p-4 md:p-6 max-w-md w-full">
              <div className="flex items-center mb-3 md:mb-4 text-red-600">
                <AlertTriangle className="h-5 w-5 md:h-6 md:w-6 mr-2" />
                <h3 className="text-base md:text-lg font-semibold">Cancel Booking</h3>
              </div>

              <p className="mb-4 md:mb-6 text-sm md:text-base">Are you sure you want to cancel this booking? This action cannot be undone.</p>

              {bookingToCancel && (
                <div className="mb-4 md:mb-6 p-3 md:p-4 bg-gray-50 rounded-lg text-sm">
                  <p className="font-medium">{bookingToCancel.villaData?.name}</p>
                  <p className="text-xs md:text-sm text-gray-600">Guest: {bookingToCancel.userName || "Guest"}</p>
                  <p className="text-xs md:text-sm text-gray-600">
                    Check-in: {formatDate(bookingToCancel.checkIn, bookingToCancel.checkInTiming)}
                  </p>
                  <p className="text-xs md:text-sm text-gray-600">
                    Check-out: {formatDate(bookingToCancel.checkOut, bookingToCancel.checkOutTiming)}
                  </p>
                  <p className="text-xs md:text-sm text-gray-600">Phone: {bookingToCancel.phoneNumber || "N/A"}</p>
                  <p className="text-xs md:text-sm text-gray-600">Booking ID: {bookingToCancel.bookingId}</p>
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  type="button"
                  onClick={() => setShowCancelDialog(false)}
                  className="w-1/2 bg-gray-500 hover:bg-gray-600 text-white text-sm py-2"
                >
                  Keep Booking
                </Button>
                <Button
                  type="button"
                  onClick={confirmCancelBooking}
                  className="w-1/2 bg-red-500 hover:bg-red-600 text-white text-sm py-2"
                >
                  Cancel Booking
                </Button>
              </div>
            </div>
          </div>
        )}

        {showInvoiceDialog && (
          <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
            <DialogContent className="max-w-[95vw] md:max-w-4xl p-0 bg-white">
              <DialogHeader className="p-4 border-b">
                <DialogTitle>Booking Invoice</DialogTitle>
              </DialogHeader>
              <div className="overflow-y-auto max-h-[80vh]">
                {selectedInvoiceData && <InvoiceGenerator data={selectedInvoiceData} isUserInvoice={true} />}
              </div>
              <DialogFooter className="p-4 border-t">
                <Button variant="outline" onClick={() => setShowInvoiceDialog(false)}>
                  Close
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {showLogoutConfirmation && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg p-4 md:p-6 max-w-md w-full">
              <h3 className="text-base md:text-lg font-semibold mb-3 md:mb-4">Confirm Logout</h3>
              <p className="mb-4 md:mb-6 text-sm md:text-base">Are you sure you want to logout?</p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  onClick={() => setShowLogoutConfirmation(false)}
                  className="w-1/2 bg-gray-500 hover:bg-gray-600 text-white text-sm py-2"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    auth.signOut();
                    navigate("/login");
                  }}
                  className="w-1/2 bg-red-500 hover:bg-red-600 text-white text-sm py-2"
                >
                  Logout
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </OwnerLayout>
  )
}
